import * as React from "react";
import { FormattedMessage } from "react-intl";
import { useStateContext } from "./state";
import { usePathContext } from "./path";
import { Button } from "@/kit/ui/button";
import { Skeleton } from "@/kit/ui/skeleton";
import { Edit02Icon, ChevronRightIcon } from "@/icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/kit/ui/table";
import { Input, InputDecorator } from "@/kit/ui/input";
import { Switch } from "@/kit/ui/switch";
import { range, isEqual } from "lodash";
import type { Schema, InferSchemaOptions } from "@aqora-io/aqora-data-utils";
import { useValibotForm, useValibotFormContext } from "@/utils/validation";
import { Form, FormField, FormControl, FormItem } from "@/kit/ui/form";
import {
  Select,
  SelectItem,
  SelectValue,
  SelectTrigger,
  SelectContent,
} from "@/kit/ui/select";
import {
  focus_edit_fields,
  FormEditField,
  SchemaFieldPrefix,
  editSchemaSchema,
  get_edit_schema,
} from "./utils/form";
import { get_overwrites } from "./utils/schema";
import { ScalarType, SimpleSchemaFieldKind } from "@/utils/data_utils_schema";
import { NaiveDate } from "@/utils/date";
import { Time } from "@/utils/time";
import { Duration, Unit as DurationUnit } from "@/utils/duration";
import { Json } from "@/components/Json";

export const SchemaEditorHeader = () => {
  return (
    <TableHeader>
      <TableRow>
        <TableHead>
          <FormattedMessage defaultMessage="Column name" />
        </TableHead>
        <TableHead>
          <FormattedMessage defaultMessage="Data type" />
        </TableHead>
        <TableHead>
          <FormattedMessage defaultMessage="Nullable" />
        </TableHead>
        <TableHead>
          <FormattedMessage defaultMessage="Example" />
        </TableHead>
      </TableRow>
    </TableHeader>
  );
};

export const SkeletonRow = () => {
  return (
    <TableRow>
      <TableCell>
        <Skeleton>Column name</Skeleton>
      </TableCell>
      <TableCell>
        <Skeleton>
          <Input />
        </Skeleton>
      </TableCell>
      <TableCell>
        <Skeleton>
          <Switch />
        </Skeleton>
      </TableCell>
      <TableCell>
        <Skeleton>Example</Skeleton>
      </TableCell>
    </TableRow>
  );
};

export const SchemaLoadingTable = ({ rows = 1 }: { rows?: number }) => {
  return (
    <Table>
      <SchemaEditorHeader />
      <TableBody>
        {range(rows).map((i) => (
          <SkeletonRow key={i} />
        ))}
      </TableBody>
    </Table>
  );
};

interface SchemaRowProps {
  field: FormEditField<SchemaFieldPrefix>;
  editable: boolean;
}

const EXAMPLE_DATE = new Date(2000, 2, 2, 3, 4, 5);

const exampleScalar = (scalar: ScalarType): unknown => {
  switch (scalar) {
    case ScalarType.Null:
      return null;
    case ScalarType.Boolean:
      return true;
    case ScalarType.Integer:
      return 123;
    case ScalarType.Float:
    case ScalarType.Decimal:
      return 123.45;
    case ScalarType.String:
      return "Lorem ipsum";
    case ScalarType.Binary:
      return new Uint8Array(128);
    case ScalarType.Date:
      return NaiveDate.fromDate(EXAMPLE_DATE);
    case ScalarType.Time:
      return Time.fromDate(EXAMPLE_DATE);
    case ScalarType.Timestamp:
      return EXAMPLE_DATE;
    case ScalarType.Duration:
      return new Duration(20, DurationUnit.Second);
  }
};

const exampleValue = <P extends string>(field: FormEditField<P>): unknown => {
  switch (field.kind) {
    case SimpleSchemaFieldKind.Scalar:
      return exampleScalar(field.scalar);
    case SimpleSchemaFieldKind.List:
      return [exampleValue(field.field)];
    case SimpleSchemaFieldKind.Struct:
      return Object.fromEntries(
        field.fields.map((field) => [field.name, exampleValue(field)]),
      );
    default:
      return undefined;
  }
};

const SchemaDataTypeInput = ({ field, editable }: SchemaRowProps) => {
  const { path, setPath } = usePathContext();
  const form = useValibotFormContext<typeof editSchemaSchema>();
  switch (field.kind) {
    case SimpleSchemaFieldKind.Scalar:
      return (
        <FormField
          control={form.control}
          name={`${field.prefix}.scalar`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Select readOnly={!editable} {...field}>
                  <SelectTrigger>
                    <SelectValue placeholder="Scalar Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(ScalarType).map((value) => (
                      <SelectItem value={value} key={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
            </FormItem>
          )}
        ></FormField>
      );
    case SimpleSchemaFieldKind.List:
    case SimpleSchemaFieldKind.Struct:
      return (
        <Input
          value={field.kind}
          readOnly
          inputProps={{ className: "pointer-events-none" }}
          endDecorator={
            <InputDecorator asChild separated>
              <Button
                variant="tertiary"
                onClick={() => {
                  setPath([...path, field.name]);
                }}
              >
                {editable ? (
                  <>
                    <Edit02Icon />
                    <FormattedMessage defaultMessage="Edit" />
                  </>
                ) : (
                  <>
                    <FormattedMessage defaultMessage="View" />
                    <ChevronRightIcon />
                  </>
                )}
              </Button>
            </InputDecorator>
          }
        />
      );
    default:
      return null;
  }
};

const SchemaRow = ({ field, editable }: SchemaRowProps) => {
  const form = useValibotFormContext<typeof editSchemaSchema>();
  const example = exampleValue(field);
  return (
    <TableRow>
      <TableCell>{field.name}</TableCell>
      <TableCell>
        <SchemaDataTypeInput field={field} editable={editable} />
      </TableCell>
      <TableCell>
        <FormField
          control={form.control}
          name={`${field.prefix}.nullable`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Switch readOnly={!editable} {...field} />
              </FormControl>
            </FormItem>
          )}
        ></FormField>
      </TableCell>
      <TableCell>
        {example != undefined && (
          <Json
            value={example}
            settings={[
              {
                longDates: false,
                showType: "none",
                rawString: true,
              },
              {
                showType: "all",
                rawString: false,
              },
            ]}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

interface SchemaEditorProps {
  schema: Schema;
  editable: boolean;
  inferSchemaOptions?: InferSchemaOptions;
  setInferSchemaOptions: (options: InferSchemaOptions) => void;
}

const SchemaEditor = ({
  schema,
  editable,
  inferSchemaOptions,
  setInferSchemaOptions,
}: SchemaEditorProps) => {
  const { path } = usePathContext();
  const editSchema = React.useMemo(() => get_edit_schema(schema), [schema]);
  const form = useValibotForm(editSchemaSchema, {
    defaultValues: editSchema,
    values: editSchema,
  });
  const overwrites = get_overwrites(
    schema,
    form.watch(),
    form.formState.dirtyFields,
    inferSchemaOptions,
  );
  React.useEffect(() => {
    if (!isEqual(overwrites, inferSchemaOptions?.overwrites)) {
      setInferSchemaOptions({ ...inferSchemaOptions, overwrites });
    }
  }, [overwrites, inferSchemaOptions, setInferSchemaOptions]);
  const fields = focus_edit_fields(editSchema, path);
  return (
    <Form className="max-h-full overflow-auto" form={form}>
      <Table>
        <SchemaEditorHeader />
        <TableBody>
          {fields.map((field) => (
            <SchemaRow key={field.name} field={field} editable={editable} />
          ))}
        </TableBody>
      </Table>
    </Form>
  );
};

export const SchemaTable = () => {
  const state = useStateContext();
  if (state.schema) {
    return (
      <SchemaEditor
        editable={state.editable}
        schema={state.schema}
        inferSchemaOptions={state.inferSchemaOptions}
        setInferSchemaOptions={state.setInferSchemaOptions}
      />
    );
  } else if (state.inferLoading) {
    return <SchemaLoadingTable />;
  } else {
    return null;
  }
};
